import { useMemo, useState } from "react";
import { ChevronDown, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { localizedProps, pick } from "src/utils/localize";

// ИНТЕРАКТИВНЫЙ ТАЙМЛАЙН ТУРА.
//
// У тура на 8–11 дней это самый длинный блок страницы. Простыня из абзацев
// подряд читается плохо: человек не видит ни общей длины поездки, ни того,
// где он находится, и не может быстро найти нужный день.
//
// Поэтому здесь три вещи, которых нет у конкурентов:
//   1) ЛЕНТА ДНЕЙ сверху — все дни разом, щелчок прокручивает к нужному;
//   2) КАРТОЧКИ, которые разворачиваются: сначала видно только город и
//      заголовок дня, подробности — по клику. Первый день открыт сразу,
//      чтобы блок не выглядел пустым;
//   3) кнопка «развернуть всё» — для тех, кто читает целиком.
//
// Раскрытие сделано на <details>/<summary>: оно работает клавиатурой и в
// скринридере само по себе, без единой строчки JavaScript, а поиск по
// странице (Ctrl+F) в современных браузерах раскрывает нужный день сам.
//
// ФОТО ДНЯ. Если в админке у дня загружено фото (`day.image`), оно
// показывается рядом с текстом. Нет фото — карточка просто текстовая,
// без серой заглушки: пустой прямоугольник хуже, чем его отсутствие.

const dayAnchor = (index) => `day-${index + 1}`;

export default function Itinerary({ itinerary, className = "" }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const days = useMemo(() => itinerary || [], [itinerary]);

  // null — каждый день сам по себе (открыт только первый);
  // true/false — «развернуть всё» / «свернуть всё» перекрывает выбор.
  const [forceOpen, setForceOpen] = useState(null);

  if (days.length === 0) return null;

  const openState = (i) => (forceOpen === null ? i === 0 : forceOpen);

  return (
    <section className={className}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
            {t("tour.itineraryTitle")}
          </h2>
          <p className="mt-1.5 text-sm text-kj-sub">
            {t("tour.itineraryDays", { count: days.length })}
          </p>
        </div>
        <button
          type="button"
          // первый щелчок всегда разворачивает всё, дальше — переключает
          onClick={() => setForceOpen((prev) => (prev === null ? true : !prev))}
          className="text-xs font-bold uppercase tracking-[0.1em] text-kj-sky hover:text-kj-gold transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
        >
          {forceOpen ? t("tour.itineraryCollapse") : t("tour.itineraryExpand")}
        </button>
      </div>

      {/* ЛЕНТА ДНЕЙ. На телефоне листается пальцем, на компьютере видна целиком.
          Это не декорация: по ней сразу понятно, что поездка на девять дней,
          а не «несколько». */}
      {days.length > 2 && (
        <nav
          aria-label={t("tour.itineraryTitle")}
          className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1"
        >
          {days.map((day, i) => {
            const title = pick(day?.title, lang);
            return (
              <a
                key={i}
                href={`#${dayAnchor(i)}`}
                className="group flex-none rounded-lg border border-kj-line bg-kj-card/40 px-3 py-2 transition-colors hover:border-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
              >
                <span className="block text-xs font-bold uppercase tracking-[0.1em] text-kj-gold tabular-nums">
                  {t("tour.dayNumber", { day: day?.day || i + 1 })}
                </span>
                <span className="mt-0.5 block max-w-[16ch] truncate text-xs text-kj-sub group-hover:text-kj-ink">
                  {title}
                </span>
              </a>
            );
          })}
        </nav>
      )}

      <ol className="mt-6 flex flex-col">
        {days.map((day, i) => {
          const title = localizedProps(day?.title, lang);
          const text = localizedProps(day?.text, lang);
          const place = localizedProps(day?.place, lang);
          const last = i === days.length - 1;

          return (
            <li key={i} id={dayAnchor(i)} className="flex gap-4 sm:gap-5 scroll-mt-24">
              {/* Колонка с номером дня и линией до следующего */}
              <div className="flex flex-none flex-col items-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-kj-gold/60 bg-kj-dark/60 text-sm font-extrabold tabular-nums text-kj-gold">
                  {day?.day || i + 1}
                </span>
                {!last && (
                  <span className="my-1.5 w-px flex-1 bg-gradient-to-b from-kj-gold/40 to-kj-line" aria-hidden="true" />
                )}
              </div>

              <div className={`min-w-0 flex-1 ${last ? "pb-0" : "pb-6"}`}>
                <details
                  // key меняется вместе с «развернуть всё» — иначе браузер
                  // сохранит прежнее состояние и кнопка не сработает
                  key={`${forceOpen}`}
                  open={openState(i)}
                  className="group rounded-xl border border-kj-line bg-kj-card/40 transition-colors open:bg-kj-card/60 hover:border-kj-gold/50"
                >
                  <summary className="flex cursor-pointer list-none items-start gap-3 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-inset rounded-xl [&::-webkit-details-marker]:hidden">
                    <span className="min-w-0 flex-1">
                      {place.value && (
                        <span
                          {...place.attrs}
                          className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-[0.1em] text-kj-sky"
                        >
                          <MapPin aria-hidden="true" className="h-3 w-3 flex-none" />
                          {place.value}
                        </span>
                      )}
                      <span
                        {...title.attrs}
                        className="block text-base sm:text-lg font-bold leading-snug text-kj-ink"
                      >
                        {title.value || t("tour.dayNumber", { day: day?.day || i + 1 })}
                      </span>
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 flex-none text-kj-gold transition-transform duration-300 group-open:rotate-180"
                    />
                  </summary>

                  {(text.value || day?.image) && (
                    <div className="grid gap-4 border-t border-kj-line/60 p-4 sm:grid-cols-[minmax(0,1fr)_auto]">
                      {text.value && (
                        <p
                          {...text.attrs}
                          className="max-w-[72ch] text-sm sm:text-base leading-relaxed text-kj-sub"
                        >
                          {text.value}
                        </p>
                      )}
                      {day?.image && (
                        <img
                          src={day.image}
                          alt=""
                          loading="lazy"
                          className="h-40 w-full rounded-lg object-cover sm:w-56"
                        />
                      )}
                    </div>
                  )}
                </details>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
